import {
  filterTagId,
  type LiveSummaryFilter,
  type LiveSummaryItemData,
  type LiveSummaryKind,
  type LiveSummaryMainData,
  type LiveSummaryTag,
  type LiveSummaryTopicData,
} from "./types";

/**
 * Top-level rows to render under a kind filter. A main whose own kind matches
 * renders as itself; an off-kind main is dropped and any of its sub-points that
 * match are promoted to their own rows in its place. This keeps a nested match
 * visible (a decision under a to-do) without rendering the off-kind parent in
 * that column — a to-do never shows in the Decisions filter, and vice versa.
 */
export function filterMains(mains: LiveSummaryMainData[], filter: LiveSummaryFilter): LiveSummaryItemData[] {
  return mains.flatMap((m) =>
    matchesFilter(m.item, filter) ? [m.item] : m.children.filter((c) => matchesFilter(c, filter))
  );
}

const FALLBACK_TOPIC = "Notes";

/**
 * Bucket items by topic (first-seen order) and nest active sub-points under their
 * main point. Set-aside items (dismissed or user-dropped) land in the topic's
 * flat `dropped` list. An active sub-point whose parent isn't itself an active
 * main renders as its own main, so nothing silently disappears.
 */
export function groupTopics(items: LiveSummaryItemData[]): LiveSummaryTopicData[] {
  const activeMainIds = new Set(items.filter((it) => it.active && !it.parentId).map((it) => it.id));

  const activeChildren = new Map<string, LiveSummaryItemData[]>();
  for (const it of items) {
    if (it.active && it.parentId && activeMainIds.has(it.parentId)) {
      const arr = activeChildren.get(it.parentId) ?? [];
      arr.push(it);
      activeChildren.set(it.parentId, arr);
    }
  }

  const order: string[] = [];
  const mains = new Map<string, LiveSummaryMainData[]>();
  const dropped = new Map<string, LiveSummaryItemData[]>();
  const ensure = (title: string): void => {
    if (!mains.has(title)) {
      mains.set(title, []);
      dropped.set(title, []);
      order.push(title);
    }
  };

  for (const it of items) {
    const title = (it.topic ?? "").trim() || FALLBACK_TOPIC;
    if (!it.active) {
      ensure(title);
      dropped.get(title)?.push(it);
      continue;
    }
    // Active sub-points render nested under their main, not at the top level.
    if (it.parentId && activeMainIds.has(it.parentId)) continue;
    ensure(title);
    mains.get(title)?.push({ item: it, children: activeChildren.get(it.id) ?? [] });
  }

  return order.map((title) => ({
    title,
    items: mains.get(title) ?? [],
    dropped: dropped.get(title) ?? [],
  }));
}

type KindCounts = Record<"all" | LiveSummaryKind, number>;

/** Count items per kind (plus "all") for the filter pills. */
export function kindCounts(items: LiveSummaryItemData[]): KindCounts {
  const c: KindCounts = { all: 0, todo: 0, decision: 0, context: 0, note: 0 };
  for (const it of items) {
    c.all++;
    c[it.kind]++;
  }
  return c;
}

/**
 * The tags something in this list actually carries, in the order the vocabulary
 * gives them, with how many items carry each. A tag nothing carries is left out
 * entirely — an empty section is not a section.
 */
export function tagCounts(
  items: LiveSummaryItemData[],
  tags: readonly LiveSummaryTag[]
): { tag: LiveSummaryTag; count: number }[] {
  const counts = new Map<string, number>();
  for (const it of items) {
    for (const id of it.tagIds ?? []) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const out: { tag: LiveSummaryTag; count: number }[] = [];
  for (const tag of tags) {
    const count = counts.get(tag.id) ?? 0;
    if (count > 0) out.push({ tag, count });
  }
  return out;
}

/** Whether an item passes the active filter — a kind, or a tag it carries. */
export function matchesFilter(item: LiveSummaryItemData, filter: LiveSummaryFilter): boolean {
  if (filter === "all") return true;
  const tagId = filterTagId(filter);
  if (tagId !== null) return (item.tagIds ?? []).includes(tagId);
  return item.kind === filter;
}

/**
 * Whether a point is still outstanding: the model wrote it down and the user has
 * neither dismissed it, set it aside, nor ticked it off. Approved counts as open
 * — approving a to-do commits to it, it doesn't finish it.
 *
 * The one reading of the item states, because every surface that hands a session
 * onwards asks the same question of them: the agent handoff and the markdown
 * download in both apps, and Chief's "handle the to-dos". A state added to the
 * vocabulary, or a ruling that one of the existing ones is settled, is a change
 * here rather than the same change made in four places.
 */
export function isOpenItem(item: LiveSummaryItemData): boolean {
  return item.active && item.state !== "dismissed" && item.state !== "done";
}

/** The text of every still-open point of one kind, in the order given — the
 *  to-do and decision lists a session export is built from. */
export function openItemTexts(items: readonly LiveSummaryItemData[], kind: LiveSummaryKind): string[] {
  return items.filter((it) => it.kind === kind && isOpenItem(it)).map((it) => it.text);
}

/** A point the room kept: not dismissed and not set aside. A set-aside point's
 * block stays in the Notes document — people delete blocks themselves — so the
 * recap and Notes' filtered views ask this rather than the document. */
export const isKeptPoint = (item: { active: boolean; state?: string; override?: string }): boolean =>
  item.active && item.state !== "dismissed" && item.override !== "dropped";
